// Frontend/src/auth/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [activeEnvironment, setActiveEnvironment] = useState(
    localStorage.getItem('activeEnvironment') || 'default'
  );
  const [isPendingFirstLogin, setIsPendingFirstLogin] = useState(false);
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('user');
    // Admin and Line Manager roles temporarily disabled
    const userData = stored ? JSON.parse(stored) : {
      id: '1',
      email: 'user@company.com',
      name: 'Test User',
      // For now we mock the role, this will come from API in future
      role: 'Employee',
      isManager: false,
      isAdmin: false,
    };
    // Ensure role is always Employee and flags are always false
    userData.role = 'Employee';
    userData.isManager = false;
    userData.isAdmin = false;
    return userData;
  });

  useEffect(() => {
    const checkAuthStatus = () => {
      const authStatus = localStorage.getItem('isAuthenticated');
      setIsAuthenticated(authStatus === 'true');
    };

    // Check on mount
    checkAuthStatus();

    // Listen for storage changes (when localStorage is updated from other tabs/components)
    const handleStorageChange = (e) => {
      if (e.key === 'isAuthenticated') {
        checkAuthStatus();
      }
    };

    // Listen for custom auth update event (for same-tab updates)
    const handleAuthUpdate = () => {
      checkAuthStatus();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authUpdate', handleAuthUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authUpdate', handleAuthUpdate);
    };
  }, []);

  const value = {
    isAuthenticated,
    isLoading,
    authError,
    activeEnvironment,
    isPendingFirstLogin,
    user,
    setIsAuthenticated,
    setActiveEnvironment,
    setIsPendingFirstLogin,
    setUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;

